// Extracted from original/src/components/ExcessDebtModal.tsx (whole file, 387 lines; ADR-08 §2.1, D-003). Used by the
// old Currency Vault (CurrencyVaultView.tsx:3698-3724, buy / deposit on a debt above its balance, D02 R-17) and the
// old quick operation (App.tsx:48042-48066, withdrawal on a debt above its balance); in the new portal also by
// Dealing in Dollar customer mode (F-247; D-218 shared dialog).
// Kept verbatim: backdrop + frame, blue header, the four-figure summary, the three options (excess in cash / pay
// another customer's debt / keep as trust) with their pickers ("+ أضف حساب جديد" first; the trust picker also offers
// "<name> (العميل الحالي نفسه)" and then the other names), the new-account inputs and the footer. Cut: as
// `ExcessTrustModal.tsx` — `lang` → `excess.*` keys, `formatNumber` → labels, `debts` → `debtOptions` /
// `trustTargets` with container keys (D-017: by id, never by name), selection state internal, handlers → callbacks
// with the resolved choice (or null). The old trust picker listed **names of every account** (`new Set(debts.map(
// d => d.name))`); `trustTargets` lists the customers (one entry per person) and the container resolves the person's
// trust account, or opens it inside the operation with PIN (D-018).
import { AnimatePresence, motion } from 'motion/react';
import { X } from 'lucide-react';
import { useT } from '../i18n-context.js';
import { resolvePick, usePick, type ExcessAccountOption, type ExcessChoice } from './ExcessTrustModal.js';

export interface ExcessDebtModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly customerName: string;
  /** Preformatted figures (old `excessDebtData.debtAmount / withdrawnAmount / excessAmount`). */
  readonly debtAmountLabel: string;
  readonly operationAmountLabel: string;
  readonly excessAmountLabel: string;
  readonly currencyLabel: string;
  readonly title?: string;
  readonly description?: string;
  readonly actionTypeLabel?: string;
  /** Debt accounts of other customers in the dialog's currency (old filter: type دين, name ≠ the customer). */
  readonly debtOptions: readonly ExcessAccountOption[];
  /** Customers offered for "keep as trust" (old: every name except the current one). */
  readonly trustTargets: readonly ExcessAccountOption[];
  /** Container key of the current customer in the trust picker ("العميل الحالي نفسه"), pre-selected as the old dialog did. */
  readonly sameCustomerKey: string;
  readonly onWithdrawCash: () => void;
  readonly onPayOtherCustomer: (choice: ExcessChoice | null) => void;
  readonly onDepositAsTrust: (choice: ExcessChoice | null) => void;
}

export function ExcessDebtModal(props: ExcessDebtModalProps) {
  const t = useT();
  const second = usePick();
  const trustPick = usePick({ id: props.sameCustomerKey, name: props.customerName });
  if (!props.open) return null;

  const curr = props.currencyLabel || 'د.ع';
  const addNew = t('excess.add_new_account');
  const debt = props.debtAmountLabel;
  const excess = props.excessAmountLabel;
  const modalTitle = props.title || t('excess.debt.title');
  const modalDescription = props.description || t('excess.debt.description');
  const modalActionLabel = props.actionTypeLabel || t('excess.debt.action');

  const matches = (p: ReturnType<typeof usePick>, name: string) => !p.text || p.text === addNew || name.toLowerCase().includes(p.text.toLowerCase());
  const sameOption: ExcessAccountOption = { id: props.sameCustomerKey, name: props.customerName };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-2 sm:p-4 pb-16 sm:pb-4 overflow-hidden" data-testid="excess-debt-modal">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={props.onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-md"
        />
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-[#0d1117] w-full max-w-xl rounded-2xl sm:rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] max-h-[84vh] sm:max-h-[88vh] flex flex-col text-right"
          dir="rtl"
        >
          <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-blue-50 dark:bg-blue-950/20 shrink-0">
            <h3 className="text-lg sm:text-xl font-black flex items-center gap-2 text-blue-700 dark:text-blue-400">
              <span>⚠️</span>
              {modalTitle}
            </h3>
            <button
              onClick={props.onClose}
              className="text-gray-400 hover:text-gray-650 dark:hover:text-[#f0f6fc] transition-colors p-1 rounded-lg hover:bg-gray-150 dark:hover:bg-[#30363d]"
            >
              <X size={20}/>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
            <div className="bg-blue-50/40 dark:bg-blue-950/5 border border-blue-200 dark:border-blue-900/30 rounded-2xl p-4 sm:p-5 space-y-3">
              <p className="text-gray-650 dark:text-gray-300 text-sm font-semibold leading-relaxed">
                {modalDescription}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs pt-2">
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block mb-1">{t('excess.customer')}</span>
                  <span className="font-bold text-gray-800 dark:text-white text-sm">{props.customerName}</span>
                </div>
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block mb-1">{t('excess.debt.current')}</span>
                  <span className="font-mono font-bold text-gray-800 dark:text-white text-sm text-blue-600">{debt} {curr}</span>
                </div>
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block mb-1">{modalActionLabel}</span>
                  <span className="font-mono font-bold text-gray-800 dark:text-white text-sm text-rose-600">{props.operationAmountLabel} {curr}</span>
                </div>
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block mb-1">{t('excess.debt.excess')}</span>
                  <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400 text-sm shrink-0 bg-emerald-50 dark:bg-emerald-950/20 px-2.5 py-0.5 rounded-lg border border-emerald-100 dark:border-emerald-900/30">{excess} {curr}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <span className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] uppercase tracking-widest text-right">
                {t('excess.debt.choose')}
              </span>

              <div className="grid grid-cols-1 gap-3">
                {/* Option 1: Withdraw Excess as Cash */}
                <button
                  type="button"
                  data-testid="excess-debt-cash"
                  onClick={props.onWithdrawCash}
                  className="w-full text-right p-4 border border-emerald-200/50 dark:border-emerald-900/30 rounded-2xl bg-emerald-50/30 dark:bg-emerald-950/10 hover:bg-emerald-50/70 dark:hover:bg-emerald-950/25 transition-all flex gap-3 group relative cursor-pointer active:scale-[0.99]"
                >
                  <span className="text-2xl mt-0.5">💵</span>
                  <div>
                    <span className="block font-black text-sm text-emerald-800 dark:text-emerald-400 group-hover:text-emerald-900 dark:group-hover:text-emerald-300">
                      {t('excess.debt.cash_title')}
                    </span>
                    <span className="block text-xs text-emerald-650 dark:text-emerald-500 mt-1">
                      {t('excess.debt.cash_desc', { debt, excess, curr })}
                    </span>
                  </div>
                </button>

                {/* Option 2: Repay Other Customer */}
                <div className="border border-blue-200/50 dark:border-blue-900/30 rounded-2xl p-4 bg-blue-50/30 dark:bg-blue-950/10 space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl mt-0.5">👤</span>
                    <div>
                      <span className="block font-black text-sm text-blue-800 dark:text-blue-400">
                        {t('excess.debt.other_title')}
                      </span>
                      <span className="block text-xs text-blue-650 dark:text-blue-500 mt-1">
                        {t('excess.debt.other_desc', { debt, excess, curr })}
                      </span>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-blue-100 dark:border-blue-900/20">
                    <label className="block text-xs font-bold text-gray-500 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                      {t('excess.debt.other_label')}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={second.text || ''}
                        onFocus={() => second.setOpen(true)}
                        onChange={(e) => {
                          second.setText(e.target.value);
                          second.setId('');
                          second.setOpen(true);
                        }}
                        className="w-full bg-white dark:bg-[#05070a] border border-blue-200 dark:border-blue-900/40 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] font-black text-sm text-right"
                        placeholder={t('excess.search_client')}
                        autoComplete="off"
                      />

                      {second.open && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => second.setOpen(false)} />
                          <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-[#0d1117] border border-gray-100 dark:border-[#30363d] rounded-2xl shadow-2xl z-50 max-h-[160px] overflow-y-auto custom-scrollbar text-right">
                            <div className="p-1 space-y-0.5">
                              <button
                                type="button"
                                onClick={() => {
                                  second.setText(addNew);
                                  second.setId('');
                                  second.setOpen(false);
                                }}
                                className="w-full text-right px-4 py-2 text-xs font-black text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20 rounded-xl transition-colors flex items-center gap-1.5"
                              >
                                <span>{addNew}</span>
                              </button>

                              {props.debtOptions
                                .filter((d) => matches(second, d.name))
                                .map((d) => (
                                  <button
                                    key={d.id}
                                    type="button"
                                    onClick={() => {
                                      second.setText(d.name);
                                      second.setId(d.id);
                                      second.setOpen(false);
                                    }}
                                    className="w-full flex justify-between items-center px-4 py-2 text-xs font-bold text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-100 dark:hover:bg-[#161b22] rounded-xl transition-colors gap-3"
                                  >
                                    <span className="truncate text-right flex-1">{d.name}</span>
                                    <span className="font-mono text-[10px] text-gray-500 dark:text-gray-450 shrink-0 bg-gray-50 dark:bg-[#161b22]/80 px-2 py-0.5 rounded-lg">
                                      {d.balanceLabel ?? '0'} {curr}
                                    </span>
                                  </button>
                                ))
                              }
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {second.text === addNew && (
                      <div className="mt-2.5 animate-in fade-in slide-in-from-top-1 duration-200">
                        <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-1.5 uppercase tracking-widest">
                          {t('excess.new_second_name_label')}
                        </label>
                        <input
                          required
                          type="text"
                          value={second.newName || ''}
                          onChange={(e) => second.setNewName(e.target.value)}
                          className="w-full bg-white dark:bg-[#05070a] border border-blue-200 dark:border-blue-900/40 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] font-black text-sm text-right"
                          placeholder={t('excess.new_name_placeholder')}
                        />
                      </div>
                    )}

                    <button
                      type="button"
                      data-testid="excess-debt-other"
                      onClick={() => props.onPayOtherCustomer(resolvePick(second, addNew, props.debtOptions))}
                      className="w-full mt-3 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 text-xs shadow-lg shadow-blue-500/10 active:scale-95 cursor-pointer"
                    >
                      <span>✅</span>
                      {t('excess.debt.other_confirm')}
                    </button>
                  </div>
                </div>

                {/* Option 4: Add Excess as Trust (أمانة) */}
                <div className="border border-amber-200/50 dark:border-amber-900/30 rounded-2xl p-4 bg-amber-50/30 dark:bg-amber-950/10 space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl mt-0.5">📥</span>
                    <div>
                      <span className="block font-black text-sm text-amber-800 dark:text-amber-400">
                        {t('excess.debt.trust_title')}
                      </span>
                      <span className="block text-xs text-amber-650 dark:text-amber-500 mt-1">
                        {t('excess.debt.trust_desc', { debt, excess, curr })}
                      </span>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-amber-100 dark:border-amber-900/20">
                    <label className="block text-xs font-bold text-gray-500 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                      {t('excess.debt.trust_label')}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={trustPick.text || ''}
                        onFocus={() => trustPick.setOpen(true)}
                        onChange={(e) => {
                          trustPick.setText(e.target.value);
                          trustPick.setId('');
                          trustPick.setOpen(true);
                        }}
                        className="w-full bg-white dark:bg-[#05070a] border border-amber-200 dark:border-amber-900/40 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-500/20 dark:text-[#f0f6fc] font-black text-sm text-right"
                        placeholder={t('excess.search_client')}
                        autoComplete="off"
                      />

                      {trustPick.open && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => trustPick.setOpen(false)} />
                          <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-[#0d1117] border border-gray-100 dark:border-[#30363d] rounded-2xl shadow-2xl z-50 max-h-[160px] overflow-y-auto custom-scrollbar text-right">
                            <div className="p-1 space-y-0.5">
                              <button
                                type="button"
                                onClick={() => {
                                  trustPick.setText(addNew);
                                  trustPick.setId('');
                                  trustPick.setOpen(false);
                                }}
                                className="w-full text-right px-4 py-2 text-xs font-black text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20 rounded-xl transition-colors flex items-center gap-1.5"
                              >
                                <span>{addNew}</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => {
                                  trustPick.setText(props.customerName);
                                  trustPick.setId(props.sameCustomerKey);
                                  trustPick.setOpen(false);
                                }}
                                className="w-full flex justify-between items-center px-4 py-2 text-xs font-black text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/10 rounded-xl transition-colors"
                              >
                                <span className="truncate text-right flex-1">{t('excess.debt.same_customer', { name: props.customerName })}</span>
                              </button>

                              {props.trustTargets
                                .filter((d) => d.id !== props.sameCustomerKey)
                                .filter((d) => matches(trustPick, d.name))
                                .map((d) => (
                                  <button
                                    key={d.id}
                                    type="button"
                                    onClick={() => {
                                      trustPick.setText(d.name);
                                      trustPick.setId(d.id);
                                      trustPick.setOpen(false);
                                    }}
                                    className="w-full flex justify-between items-center px-4 py-2 text-xs font-bold text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-100 dark:hover:bg-[#161b22] rounded-xl transition-colors gap-3"
                                  >
                                    <span className="truncate text-right flex-1">{d.name}</span>
                                  </button>
                                ))
                              }
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {trustPick.text === addNew && (
                      <div className="mt-2.5 animate-in fade-in slide-in-from-top-1 duration-200">
                        <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-1.5 uppercase tracking-widest">
                          {t('excess.debt.trust_new_label')}
                        </label>
                        <input
                          required
                          type="text"
                          value={trustPick.newName || ''}
                          onChange={(e) => trustPick.setNewName(e.target.value)}
                          className="w-full bg-white dark:bg-[#05070a] border border-amber-200 dark:border-amber-900/40 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-500/20 dark:text-[#f0f6fc] font-black text-sm text-right"
                          placeholder={t('excess.new_name_placeholder')}
                        />
                      </div>
                    )}

                    <button
                      type="button"
                      data-testid="excess-debt-trust"
                      onClick={() => props.onDepositAsTrust(resolvePick(trustPick, addNew, [sameOption, ...props.trustTargets]))}
                      className="w-full mt-3 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 text-xs shadow-lg shadow-amber-500/10 active:scale-95 cursor-pointer"
                    >
                      <span>✅</span>
                      {t('excess.debt.trust_confirm')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-gray-100 dark:border-[#30363d] bg-gray-50/50 dark:bg-[#090d13] flex justify-end shrink-0">
            <button
              type="button"
              onClick={props.onClose}
              className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-750 text-gray-700 dark:text-gray-300 rounded-xl font-bold transition-all text-xs active:scale-[0.98]"
            >
              {t('excess.close')}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
